import { useTw } from '../Styles'
import { useCallback } from 'react'
import { useDropdown } from '../Dropdown'
import { ShareIcon, ClipboardCopyIcon } from '@heroicons/react/outline'

type Props = {
  title: string
  text: string
  url?: string
}

const features = `height=700,width=700`

function Facebook({ onClick }: { onClick: () => void }) {
  const tw = useTw()
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="20"
      fill="none"
      onClick={onClick}
      className={tw`cursor-pointer`}
    >
      <path
        fill="#4C4859"
        d="M8.175 3.32h1.826V.14A23.576 23.576 0 007.34 0C4.708 0 2.905 1.656 2.905 4.7v2.8H0v3.555h2.905V20h3.562v-8.944h2.787L9.697 7.5H6.466V5.05c0-1.027.277-1.73 1.709-1.73z"
      ></path>
    </svg>
  )
}

function Linkedin({ onClick }: { onClick: () => void }) {
  const tw = useTw()
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="none"
      onClick={onClick}
      className={tw`cursor-pointer`}
    >
      <path
        fill="#4C4859"
        d="M18.19 0H1.81C.81 0 0 .81 0 1.81v16.38C0 19.19.81 20 1.81 20h16.38c1 0 1.81-.81 1.81-1.81V1.81c0-1-.81-1.81-1.81-1.81zm-12 17.27c0 .29-.236.526-.527.526h-2.24a.526.526 0 01-.527-.527V7.875c0-.291.235-.527.526-.527h2.241c.291 0 .527.236.527.527v9.394zM4.542 6.461a2.13 2.13 0 110-4.258 2.13 2.13 0 010 4.258zM17.9 17.312a.484.484 0 01-.484.484h-2.405a.484.484 0 01-.484-.484v-4.407c0-.657.193-2.88-1.718-2.88-1.482 0-1.783 1.521-1.843 2.204v5.083a.484.484 0 01-.484.484H8.157a.484.484 0 01-.484-.484v-9.48c0-.267.216-.484.484-.484h2.326c.267 0 .484.217.484.484v.82c.55-.825 1.366-1.462 3.105-1.462 3.851 0 3.829 3.598 3.829 5.575v4.547z"
      ></path>
    </svg>
  )
}

function Twitter({ onClick }: { onClick: () => void }) {
  const tw = useTw()
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="20"
      fill="none"
      onClick={onClick}
      className={tw`cursor-pointer`}
    >
      <path
        fill="#4C4859"
        d="M24.615 2.368c-.915.401-1.89.667-2.907.797a5.018 5.018 0 002.22-2.79c-.976.582-2.053.993-3.2 1.222a5.045 5.045 0 00-8.73 3.45c0 .4.034.785.117 1.151C7.92 5.994 4.208 3.983 1.714.92a5.08 5.08 0 00-.69 2.55c0 1.748.9 3.298 2.24 4.195a4.984 4.984 0 01-2.28-.622v.055a5.07 5.07 0 004.044 4.959 5.054 5.054 0 01-1.323.166c-.323 0-.65-.018-.956-.086.654 1.998 2.51 3.468 4.716 3.515a10.14 10.14 0 01-6.257 2.153c-.414 0-.811-.019-1.208-.07A14.207 14.207 0 007.742 20c9.286 0 14.363-7.692 14.363-14.36 0-.223-.008-.438-.019-.652a10.068 10.068 0 002.53-2.62z"
      ></path>
    </svg>
  )
}

export function Share({ title, text, url }: Props) {
  const [ref, open, setOpen] = useDropdown<HTMLDivElement>()
  const tw = useTw()
  const u = encodeURIComponent(url)
  const t = encodeURIComponent(text)
  const tt = encodeURIComponent(title)

  const onHandleShare = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if ('share' in navigator) {
        navigator.share({
          title,
          text,
          url,
        })
      } else {
        e.stopPropagation()
        setOpen(true)
      }
    },
    [setOpen, text, title, url]
  )

  return (
    <div className={tw`relative`}>
      <div className={tw`cursor-pointer`} onClick={onHandleShare}>
        <ShareIcon className={tw`w-6 h-6 text-neutral-400`} />
      </div>
      {open && (
        <div
          className={tw`rounded-full shadow px-2 py-4 absolute flex flex-col items-center bg-white bottom-0 left-[50%] translate-x-[-50%] space-y-4 text-neutral-400`}
          ref={ref}
        >
          <ClipboardCopyIcon
            className={tw`cursor-pointer`}
            onClick={() =>
              navigator?.clipboard
                ?.writeText(
                  `${title}: ${url}
                  ${text}`
                )
                .then(() => setOpen(false))
                .catch(() => setOpen(false))
            }
          />
          <Facebook
            onClick={() => {
              window.open(
                `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`,
                'Facebook',
                features
              )
              setOpen(false)
            }}
          />
          <Linkedin
            onClick={() => {
              window.open(
                `https://www.linkedin.com/shareArticle?mini=true&url=${u}&title=${tt}&summary=${t}&source=LinkedIn`,
                'LinkedIn',
                features
              )
              setOpen(false)
            }}
          />
          <Twitter
            onClick={() => {
              window.open(
                `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
                'Twitter',
                features
              )
              setOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
